import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IntegrationProvider } from '../../../../data/models/integrations.model';
import { AccountingSyncSettings } from '../../../../data/repositories/repo-accountingSync.service';
import { GetAccountingBillInvoicesResult } from './api/queries/GetAccountingBillInvoices.generated';
import { JobDropdownResult } from './api/queries/JobDropdown.generated';
import { JobItemsDropdownResult } from './api/queries/JobItemsDropdown.generated';

export type InvoiceMapping = {
    job?: JobInfo;
    category?: Category | string;
    item?: CostingItem | string;
};
export type LineItemMapping = InvoiceMapping & {
    invoiceId: string;
};

export type JobInfo = NonNullable<NonNullable<JobDropdownResult['jobs']>['items']>[number];
export type Category = NonNullable<NonNullable<JobItemsDropdownResult['jobItems']>['items']>[number];
export type CostingItem = NonNullable<NonNullable<JobItemsDropdownResult['jobItems']>['items']>[number];
export type Invoice = NonNullable<NonNullable<GetAccountingBillInvoicesResult['accountingBillInvoices']>['invoices']>[number];
export type LineItem = NonNullable<Invoice['lineItems']>[number];

type JobItems = JobDropdownResult['jobs'];

@Injectable({ providedIn: 'root' })
export class AccountingImportStoreService {
    initialJobItems = new InitialJobItemDropdown();
    accountingInvoices = new AccountingInvoices();
    invoiceMap = new InvoiceMap();
    lineItemMap = new LineItemMap();
    provider = new Provider();
    syncSettings = new SyncSettings();
    reset() {
        this.initialJobItems.set(null);
        this.accountingInvoices.set(null);
        this.invoiceMap.reset();
        this.lineItemMap.reset();
        this.provider.set(null);
        this.syncSettings.set(null);
    }
}

type AccountingInvoiceState = {
    items: Invoice[];
    page: number;
    hasNextPage: boolean;
};

class AccountingInvoices {
    #source = new BehaviorSubject<AccountingInvoiceState | null>(null);
    readonly obs$ = this.#source.asObservable();
    readonly #existingIds = new Set<string>();
    set(val: AccountingInvoiceState | null) {
        if (!val) this.#existingIds.clear();
        val?.items.forEach((x) => this.#existingIds.add(x.id));
        this.#source.next(val);
    }
    get value() {
        return this.#source.value;
    }
    invoiceExists(id: string) {
        return this.#existingIds.has(id);
    }
    delete(id: string) {
        if (!this.value) return;
        const items = this.value.items?.filter((v) => v.id !== id) ?? [];
        this.#existingIds.delete(id);
        this.set({ ...this.value, items });
    }
}

class InitialJobItemDropdown {
    #source = new BehaviorSubject<JobItems>(null);
    readonly obs$ = this.#source.asObservable();
    set(val: JobItems | null) {
        this.#source.next(val);
    }
    get value() {
        return this.#source.value;
    }
}

class InvoiceMap {
    #source = new BehaviorSubject(new Map<string, InvoiceMapping>());
    readonly obs$ = this.#source.asObservable();
    set(id: string, mapping: InvoiceMapping) {
        this.#source.value.set(id, mapping);
        this.#source.next(this.#source.value);
    }
    delete(id: string) {
        this.#source.value.delete(id);
        this.#source.next(this.#source.value);
    }
    get value() {
        return this.#source.value;
    }
    reset() {
        this.#source.value.clear();
        this.#source.next(this.#source.value);
    }
}

class LineItemMap {
    #source = new BehaviorSubject(new Map<string, LineItemMapping>());
    readonly obs$ = this.#source.asObservable();
    set(id: string, mapping: LineItemMapping) {
        this.#source.value.set(id, mapping);
        this.#source.next(this.#source.value);
    }
    delete(id: string) {
        this.#source.value.delete(id);
        this.#source.next(this.#source.value);
    }
    get value() {
        return this.#source.value;
    }
    getPartialById(id: string): { category: Partial<Category>; item: Partial<CostingItem> } | null {
        const lineItem = this.value.get(id);
        if (!lineItem) return null;
        const category = typeof lineItem.category === 'object' ? lineItem.category : { description: lineItem.category };
        const item = typeof lineItem.item === 'object' ? lineItem.item : { description: lineItem.item };
        return { category, item };
    }
    reset() {
        this.#source.value.clear();
        this.#source.next(this.#source.value);
    }
}

class Provider {
    #source = new BehaviorSubject<IntegrationProvider | null>(null);
    readonly obs$ = this.#source.asObservable();
    set(val: IntegrationProvider | null) {
        this.#source.next(val);
    }
    get value() {
        return this.#source.value;
    }
}

class SyncSettings {
    #source = new BehaviorSubject<AccountingSyncSettings | null>(null);
    readonly obs$ = this.#source.asObservable();
    set(val: AccountingSyncSettings | null) {
        this.#source.next(val);
    }
    get value() {
        return this.#source.value;
    }
}
